
export interface ReportStats {
  numberOfDamageLocations: number;
  numberOfSavedMileages: number;
  numberOfCountries: number;
  numberOfEntriesWithTotalLoss: number;
  numberOfDates: number;
  numberOfEntries: number;
}

export interface ReportStatsV2 extends ReportStats {
  totalDamage: {
    value: number;
    currencyIsoCode: string;
    currencyName: string;
  }[];
}

export interface Damage {
  rear: boolean;
  roof: boolean;
  front: boolean;
  sideLeft: boolean;
  interior?: boolean;
  rearLeft?: boolean;
  frontLeft?: boolean;
  rearRight?: boolean;
  underbody?: boolean;
  sideRight?: boolean;
  frontRight?: boolean;
  mechanical?: boolean;
}

export interface DamagesInfo {
  glass: Damage;
  general: Damage;
  positionsIds?: string[];
  description?: string;
}

export interface DamageOnImage {
  mainImage: string;
  pointsImages: string[];
}

export interface Entry {
  creationDate: string;
  displayDate: string;
  countryCode: string;
  countryName: string;
  mileage: number;
  damageValueRange: string;
  damageGroups: string[];
  damagePositions: string[];
  carModel: string;
  carManufacturer: string;
  damagesNames: string[];
  damages: DamagesInfo;
  damagesOnImage: DamageOnImage[];
}

export interface BasicCarInformationDto {
  vin: string;
  brand: string;
  model: string;
}

export interface EntryV2 extends Entry {
  currencyIsoCode: string;
  currencyName: string;
  /** @deprecated Use currencyIsoCode instead */
  currency: string;
  id: string;
  claimId?: string;
}

export interface Photos {
  baseUrl: string;
  claims: Record<string, string[]>;
}

export interface ApiResponseV1 {
  provider: 'HSP';
  version?: "v1"; 
  content: {
    reportStats: ReportStats;
    entries: Entry[];
    basicCarInformationDto: BasicCarInformationDto;
  };
}

export interface ApiResponseV2 {
  provider: 'HSP';
  version: "v2";
  content: {
    reportStats: ReportStatsV2;
    entries: Entry[];
    basicCarInformationDto: BasicCarInformationDto;
  };
}

export type ApiResponse = ApiResponseV1 | ApiResponseV2;

// Vehicle Timeline Types
export enum VehicleTimelineEventType {
  DAMAGE = "Damage",
  SERVICE = "SERVICE",
  REGISTRATION = "REGISTRATION",
  INSPECTION = "INSPECTION",
  ACCIDENT = "ACCIDENT",

  // cepik
  FirstOwner = "FirstOwner",
  FirstRegistration = "FirstRegistration",
  FirstRegistrationInPoland = "FirstRegistrationInPoland",
  PeriodicTechnicalInspection = "PeriodicTechnicalInspection",
  OwnerChange = "OwnerChange",
  InsuranceExpiry = "InsuranceExpiry",
  CoOwnerAdded = "CoOwnerAdded",
  OdometerReadingDuringInspection = "OdometerReadingDuringInspection",
  PeriodicTechnicalInspectionOdometerRollback = "PeriodicTechnicalInspectionOdometerRollback",
  OwnershipTransfer = "OwnershipTransfer",
  Sale = "Sale",
  Other = "Other",
}

export interface VehicleTimelineEntry {
  type: VehicleTimelineEventType;
  claimId: string;
  date: string;
  name?: string;
  mileage?: number;
  damages?: {
    types: string[];
    value: string;
  };
  details?: Record<string, unknown>;
  description?: string[] | string;
  location?: string;
}

export interface VehicleTimelineDocument {
  type: string;
  issuedDate: string;
  status: string;
}

export interface VehicleTimeline {
  entries: VehicleTimelineEntry[];
  registrationStatus?: string;
  technicalInspectionCurrent?: string;
  insuranceExpiryDate?: string;
  hasCurrentOCPolicy?: boolean;
  document?: VehicleTimelineDocument;
}

// Technical Parameters Types
export interface BodyParameters {
  bodyType: string;
  bodyStyle: string;
  doorsCount: number;
  seatsCount: {
    total: number;
    seating: number;
  };
  bodyVersion: string;
}

export interface OtherParameters {
  steeringType: string;
  batteryVoltage: number;
  optionalEquipmentImpact: string[];
}

export interface DimensionsAndMass {
  widthMm: number;
  heightMm: number;
  lengthMm: number;
  payloadKg: number;
  rearTrackMm: number;
  wheelbaseMm: number;
  curbWeightKg: number;
  frontTrackMm: number;
  trunkCapacityL: number;
  grossVehicleWeightKg: number;
  trailerLoadWithBrakesKg: number;
  trailerLoadWithoutBrakesKg: number;
}

export interface WheelsAndSuspension {
  axleCount: number;
  wheelSize: string;
  spareWheel: boolean;
  tyreSizeRear: string;
  brakeTypeRear: string;
  tyreSizeFront: string;
  tyreWidthRear: number;
  wheelsPerAxle: number[];
  brakeTypeFront: string;
  rimWidthRearMm: number;
  tyreWidthFront: number;
  rimWidthFrontMm: number;
  tyreProfileRear: number;
  tyreProfileFront: number;
  rimDiameterRearMm: number;
  rimDiameterFrontMm: number;
  suspensionTypeRear: string;
  suspensionTypeFront: string;
  tyreConstructionRear: string;
  tyreConstructionFront: string;
}

export interface VehicleIdentification {
  vin: string;
  version: string;
  modelYear: number;
  generation: string;
  faceliftDate: string;
  brandAndModel: string;
  productionYear: number;
  productionEndDate: string;
  productionStartDate: string;
  countryOfManufacture: string;
}

export interface FuelConsumptionAndEmissions {
  co2GPerKm: number;
  cityLPer100Km: number;
  emissionStandard: string;
  estimatedRangeKm: number;
  combinedLPer100Km: number;
  fuelTankCapacityL: number;
  extraUrbanLPer100Km: number;
}

export interface TechnicalParameters {
  body: BodyParameters;
  other: OtherParameters;
  dimensionsAndMass: DimensionsAndMass;
  wheelsAndSuspension: WheelsAndSuspension;
  vehicleIdentification: VehicleIdentification;
  fuelConsumptionAndEmissions: FuelConsumptionAndEmissions;
}

// Equipment Types

/**
 * Typ określający rodzaj wyposażenia
 */
export type EquipmentType = "Standard" | "Optional";

/**
 * Interfejs reprezentujący pojedynczy element wyposażenia pojazdu
 */
export interface Equipment {
  /** Kod wyposażenia */
  code: string;
  /** Nazwa wyposażenia */
  name: string;
  /** Kategoria wyposażenia (np. Safety, Comfort, Interior) - opcjonalna, jeśli brak to zostanie użyta kategoria "Inne" */
  category: string;
  /** Typ wyposażenia - standardowe lub opcjonalne */
  equipmentType: EquipmentType;
  /** Cena katalogowa wyposażenia */
  catalogPrice?: number;
  /** Aktualna wartość rynkowa wyposażenia */
  currentMarketPrice?: number;
}

/**
 * Interfejs reprezentujący pakiet wyposażenia pojazdu
 */
export interface EquipmentPacket {
  /** Kod pakietu wyposażenia */
  code: string;
  /** Nazwa pakietu wyposażenia */
  name: string;
  /** Lista elementów wyposażenia wchodzących w skład pakietu */
  equipments: Equipment[];
  /** Cena katalogowa pakietu */
  catalogPrice?: number;
  /** Aktualna wartość rynkowa pakietu */
  currentMarketPrice?: number;
  /** Typ pakietu wyposażenia - standardowy lub opcjonalny */
  equipmentPacketType?: EquipmentType;
}

export interface EquipmentData {
  optional?: {
    packets?: EquipmentPacket[];
    equipments?: Equipment[];
    summary?: {
      equipmentsTotalGrossPrice?: number;
      packetsTotalGrossPrice?: number;
    };
  };
  standard?: {
    packets?: EquipmentPacket[];
    equipments?: Equipment[];
  };
}

// Evaluation Types
export interface Depreciation {
  rate: number;
  value: number;
}

export interface EquipmentImpact {
  rate: number;
  value: number;
}

export interface Evaluation {
  currency: string;
  baseCarValue: number;
  depreciation: Depreciation;
  evaluationDate: string;
  equipmentImpact: EquipmentImpact;
  currentMarketValue: number;
  technicalValueAdjusted: number;
  newCarValueWithEquipment: number;
}

// Theft Databases Info Types
export enum TheftDatabaseStatus {
  NOT_FOUND_IN_STOLEN_DB = "NOT_FOUND_IN_STOLEN_DB",
  FOUND_IN_STOLEN_DB = "FOUND_IN_STOLEN_DB",
  PROBLEM_WITH_DB_CONNECTION = "PROBLEM_WITH_DB_CONNECTION",
}

export interface DatabaseCheck {
  database: string;
  status: TheftDatabaseStatus | string;
  displayName: string;
  comment?: string;
  recommendation?: string;
}

export interface TheftDatabasesInfo {
  stolenDatabases: DatabaseCheck[];
  pledgeRegisters: DatabaseCheck[];
  checkedAt: string;
}

// Warnings and Recommendations Types
export type RiskSeverity = "High" | "Medium" | "Low";

export type ProblemStatus = "Exists" | "NotExists" | "MissingData";

export interface WarningItem {
  code: string;
  severity: RiskSeverity;
  problemStatus: ProblemStatus;
}

export interface WarningsAndRecommendations {
  riskSeverity: RiskSeverity;
  items: {
    High: WarningItem[];
    Medium: WarningItem[];
    Low: WarningItem[];
  };
}

// Key Indicators Types
export interface ClaimsIndicators {
  count: number;
  mostExpensive: number;
}

export interface OdometerIndicators {
  lastReading: number;
  lastReadingDate: string;
  rollbackDetected: boolean;
}

export interface KeyIndicators {
  claims: ClaimsIndicators;
  odometer: OdometerIndicators;
}

export interface ApiResponseRaportV2 {
  provider: 'HSP';
  version: "v2";
  createdDate: string;
  content: {
    reportStats: ReportStatsV2;
    entries: EntryV2[];
    basicCarInformationDto?: BasicCarInformationDto;
    photos?: Photos;
    vehicleTimeline?: VehicleTimeline;
    technicalParameters?: TechnicalParameters;
    equipment?: EquipmentData;
    evaluation?: Evaluation;
    theftDatabasesInfo?: TheftDatabasesInfo;
    warningsAndRecommendations?: WarningsAndRecommendations;
    keyIndicators?: KeyIndicators;
  };
}
